import { Button, Form, Input, Upload } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  useRegisterMutation,
  useUploadFileMutation,
} from "../../../../app/features/auth/auth.api";
import { ENV_CONFIG } from "../../../../config/global.config";
import { toast } from "../../../../lib/toast";
import { errorToast } from "../../../../utils/error.utils";
import { normalizePhoneNumber } from "../../../../utils/string.utils";
import OtpChannelModal from "../../components/OtpChannelModal";
import ImageIcon from "../_icons/ImageIcon";
import UserPassportImage from "../_icons/passport-image.svg";
import QuestionMarkIcon from "../_icons/QuestionMarkIcon";
import UploadIcon from "../_icons/UploadIcon";
import UserPassport2 from "../_icons/user-2-passport-pin-icon.svg";
import UserImage from "../_icons/user-pin-icon.svg";
import CameraCaptureModal from "./camera-capture-modal";

export default function RegisterForm() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [register, { isLoading }] = useRegisterMutation();
  const [uploadPassportFile, { isLoading: isPassportUploading }] =
    useUploadFileMutation();
  const [uploadPhotoFile, { isLoading: isPhotoUploading }] =
    useUploadFileMutation();
  const [passportUrl, setPassportUrl] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [showPassportSuccess, setShowPassportSuccess] = useState(false);
  const [showPhotoSuccess, setShowPhotoSuccess] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [tempFormData, setTempFormData] = useState<any>(null);

  const handleUpload = async (
    options: any,
    setUrl: (url: string) => void,
    uploadFn: any,
    setShowSuccess?: (show: boolean) => void,
  ) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await uploadFn(formData).unwrap();
      if (response?.file?.url) {
        const fullUrl = `${ENV_CONFIG.IMAGE_URL}/${response.file.url}`;
        setUrl(fullUrl);
        if (setShowSuccess) {
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 5000);
        }
        onSuccess("ok");
        toast("success", "আপলোড সফল হয়েছে!");
      } else {
        onError("Upload failed");
        toast("error", "আপলোড ব্যর্থ হয়েছে: কোনো URL পাওয়া যায়নি");
      }
    } catch (error) {
      console.error("Upload Error:", error);
      onError(error);
      toast("error", "আপলোড ব্যর্থ হয়েছে!");
    }
  };

  const handleCameraCapture = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await uploadPhotoFile(formData).unwrap();
      if (response?.file?.url) {
        const fullUrl = `${ENV_CONFIG.IMAGE_URL}/${response.file.url}`;
        setPhotoUrl(fullUrl);
        setShowPhotoSuccess(true);
        setTimeout(() => setShowPhotoSuccess(false), 5000);
        toast("success", "আপলোড সফল হয়েছে!");
      } else {
        toast("error", "আপলোড ব্যর্থ হয়েছে: কোনো URL পাওয়া যায়নি");
      }
    } catch (error) {
      console.error("Upload Error:", error);
      toast("error", "আপলোড ব্যর্থ হয়েছে!");
    }
  };

  const submitRegistration = async (payload: any) => {
    try {
      await register(payload).unwrap();
      navigate("/registration-otp", {
        state: {
          phone: payload.phone,
          email: payload.email,
          otpChannel: payload.otpChannel,
        },
      });
      return true;
    } catch (error) {
      errorToast(error, "নিবন্ধন ব্যর্থ হয়েছে!");
      return false;
    }
  };

  const handleSubmit = async (values: any) => {
    if (!passportUrl) {
      toast("error", "পাসপোর্ট কপি আপলোড করুন!");
      return;
    }
    if (!photoUrl) {
      toast("error", "ছবি আপলোড করুন!");
      return;
    }

    const payload = {
      ...values,
      phone: normalizePhoneNumber(values.phone),
      profilePhotoUrl: photoUrl,
      passportFileUrl: passportUrl,
    };

    // if (ENV_CONFIG.NODE_ENV === "development") {
    //   setTempFormData(payload);
    //   setIsOtpModalOpen(true);
    //   return;
    // }

    setTempFormData(payload);
    setIsOtpModalOpen(true);
  };

  const handleOtpSubmit = async (values: { otpChannel: string }) => {
    try {
      const finalPayload = {
        ...tempFormData,
        otpChannel: values.otpChannel,
      };
      const success = await submitRegistration(finalPayload);
      if (success) {
        setIsOtpModalOpen(false);
      }
    } catch (error) {
      // Validation failed
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-24">
      <div className="flex flex-col-reverse lg:flex-row justify-center items-start gap-8 lg:gap-4">
        <div className="md:w-[480px] p-6 bg-gray-50 rounded-xl border border-theme-zinc-200 ">
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="fullName"
              label="আপনার পূর্ণ নাম (ইংরেজী)"
              rules={[
                {
                  required: true,
                  message: "আপনার পূর্ণ নাম দিন",
                },
              ]}
            >
              <Input
                size="large"
                placeholder="আপনার পূর্ণ নাম দিন"
                className="antd-input-primary"
              />
            </Form.Item>
            <Form.Item
              label="মোবাইল নম্বর"
              name="phone"
              validateFirst
              rules={[
                { required: true, message: "মোবাইল নম্বর দিতে হবে!" },
                {
                  pattern: /^(?:\+880|880|0)1[3-9]\d{8}$/,
                  message: "বৈধ বাংলাদেশি মোবাইল নম্বর দিন",
                },
              ]}
            >
              <Input
                placeholder="+8801XXXXXXXXX"
                size="large"
                className="antd-input-primary"
              />
            </Form.Item>

            <Form.Item
              label="ইমেইল"
              name="email"
              validateFirst
              rules={[
                { required: true, message: "ইমেইল দিতে হবে!" },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    if (!value.trim()) {
                      return Promise.reject(
                        new Error("ইমেইল খালি রাখা যাবে না"),
                      );
                    }
                    if (/[._-]{2,}/.test(value)) {
                      return Promise.reject(
                        new Error(
                          "একাধিক বিশেষ অক্ষর (., _, -) একসাথে ব্যবহার করা যাবে না",
                        ),
                      );
                    }
                    if (value.startsWith("-")) {
                      return Promise.reject(
                        new Error("ইমেইল হাইফেন (-) দিয়ে শুরু হতে পারে না"),
                      );
                    }
                    if ((value.match(/@/g) || []).length !== 1) {
                      return Promise.reject(
                        new Error("ইমেইলে একটি মাত্র @ থাকতে হবে"),
                      );
                    }
                    if (value.startsWith("@")) {
                      return Promise.reject(
                        new Error("ইমেইলের লোকাল পার্ট থাকতে হবে"),
                      );
                    }
                    if (value.startsWith(".") || value.endsWith(".")) {
                      return Promise.reject(
                        new Error("ইমেইল ডট (.) দিয়ে শুরু বা শেষ হতে পারে না"),
                      );
                    }
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) {
                      return Promise.reject(
                        new Error(
                          "সঠিক ইমেইল ফরম্যাট দিন (example@domain.com)",
                        ),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="name@example.com"
                size="large"
                className="antd-input-primary"
              />
            </Form.Item>

            <div className="flex gap-4 h-32 mb-6 custom-upload">
              {/* Left Image */}
              <div className="w-28 h-full">
                <img
                  src={photoUrl || UserImage}
                  alt="User"
                  className="w-full h-full object-cover rounded !border-[0.56px] border-theme-slate-400"
                />
              </div>

              {/* Right Upload Box */}
              <div
                className="flex-1 h-full bg-white rounded-md
             border-2 border-dashed border-theme-primary
             flex items-center gap-8 px-6"
              >
                <div className="flex flex-col items-center justify-center gap-1 text-center w-full">
                  <div className="flex items-center gap-4">
                    <Upload
                      customRequest={(options) =>
                        handleUpload(
                          options,
                          setPhotoUrl,
                          uploadPhotoFile,
                          setShowPhotoSuccess,
                        )
                      }
                      showUploadList={false}
                      accept="image/*"
                    >
                      <div className="flex flex-col items-center cursor-pointer">
                        <UploadIcon />
                        <span className="text-theme-primary text-base font-medium">
                          ফটো আপলোড
                        </span>
                      </div>
                    </Upload>

                    <div className="w-px h-10 bg-slate-400" />

                    <div
                      className="flex flex-col items-center cursor-pointer"
                      onClick={() => setIsCameraOpen(true)}
                    >
                      <ImageIcon />
                      <span className="text-theme-primary text-base font-medium">
                        ফটো তুলুন
                      </span>
                    </div>
                  </div>
                  {isPhotoUploading ? (
                    <span className="text-theme-primary text-xs animate-pulse">
                      আপলোড হচ্ছে...
                    </span>
                  ) : (
                    showPhotoSuccess &&
                    photoUrl && (
                      <span className="text-green-600 text-xs">
                        আপলোড সফল হয়েছে!
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-4 h-32 mb-8 custom-upload">
              {/* Left Image */}
              <div className="w-28 h-full">
                <img
                  src={passportUrl || UserPassport2}
                  alt="User"
                  className="w-full h-full object-cover rounded !border-[0.56px] border-theme-slate-400"
                />
              </div>

              {/* Right Upload Box */}
              <div className="flex-1 h-full">
                <Upload.Dragger
                  customRequest={(options) =>
                    handleUpload(
                      options,
                      setPassportUrl,
                      uploadPassportFile,
                      setShowPassportSuccess,
                    )
                  }
                  showUploadList={false}
                  accept="image/*"
                  className="!bg-white !rounded-md !border-theme-primary !h-full"
                >
                  <div className="flex flex-col items-center justify-center gap-1 text-center py-4">
                    {isPassportUploading ? (
                      <span className="text-theme-primary animate-pulse">
                        আপলোড হচ্ছে...
                      </span>
                    ) : showPassportSuccess && passportUrl ? (
                      <span className="text-green-600 font-medium">
                        আপলোড সফল হয়েছে!
                      </span>
                    ) : (
                      <>
                        <UploadIcon />
                        <span className="text-theme-primary text-base font-medium">
                          পাসপোর্ট কপি আপলোড করুন
                        </span>
                      </>
                    )}
                  </div>
                </Upload.Dragger>
              </div>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              className="pt-2"
              loading={isLoading}
            >
              পরবর্তী
            </Button>
          </Form>
        </div>

        <div className="w-full hidden lg:w-56 p-3 bg-yellow-50 rounded-lg border border-theme-yellow-500 md:flex flex-col gap-2">
          <img
            className="h-full lg:h-72 w-full object-cover border-[0.56px] border-slate-400"
            src={UserPassportImage}
            alt="Passport"
          />

          <div className="inline-flex justify-start items-start gap-1.5">
            <QuestionMarkIcon />
            <div className="flex-1 text-yellow-600 text-sm font-normal">
              পাসপোর্ট এর প্রোফাইল পেইজ টি পরিষ্কার স্ক্যান/ছবি তুলে আপলোড করুন।
            </div>
          </div>
        </div>
      </div>
      <CameraCaptureModal
        open={isCameraOpen}
        onClose={() => setIsCameraOpen(false)}
        onCapture={handleCameraCapture}
      />

      <OtpChannelModal
        open={isOtpModalOpen}
        onClose={() => setIsOtpModalOpen(false)}
        onFinish={handleOtpSubmit}
        isLoading={isLoading}
        email={tempFormData?.email}
        phone={tempFormData?.phone}
      />
    </div>
  );
}
